import { type RefObject, useCallback, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { useDebouncedCallback } from "use-debounce";

export const useScrollToBottom = (scrollableContainerRef: RefObject<HTMLDivElement | null>) => {
    const [isAtBottom, setIsAtBottom] = useState(true);
    const showButton = useMemo(() => !isAtBottom, [isAtBottom]);

    /**
     * Effect to scroll to the bottom of the container when the conversation is loaded.
     */
    useLayoutEffect(() => {
        if (scrollableContainerRef.current) {
            scrollableContainerRef.current.scrollTo(0, scrollableContainerRef.current.scrollHeight);
        }
    }, [scrollableContainerRef]);

    const handleScroll = useDebouncedCallback(() => {
        const container = scrollableContainerRef.current;
        if (container) {
            const devicePixelRatio = window.devicePixelRatio || 1;
            const tolerance = 3;
            const atBottom = Math.floor((container.scrollHeight - container.scrollTop) / devicePixelRatio) <=
            Math.floor((container.clientHeight / devicePixelRatio)) + tolerance;
            setIsAtBottom(atBottom);
        }
    }, 50);

    const scrollToBottom = useCallback(() => {
        scrollableContainerRef.current?.scrollTo({
            top: scrollableContainerRef.current.scrollHeight,
            behavior: "smooth"
        });
    }, [scrollableContainerRef]);

    useEffect(() => {
        const container = scrollableContainerRef.current;

        if (!container) {
            return;
        }

        // Handle cases where the container is resized (zoom/dezoom, window resize, etc.)
        const resizeObserver = new ResizeObserver(() => {
            handleScroll();
        });

        container.addEventListener("scroll", handleScroll);
        resizeObserver.observe(container);
        handleScroll();

        return () => {
            container.removeEventListener("scroll", handleScroll);
            resizeObserver.disconnect();
        };
    }, [scrollableContainerRef, handleScroll]);

    return {
        showButton,
        scrollToBottom
    };
};
